import React, { useState, useEffect } from "react";
import { Button, Modal, ModalHeader, ModalBody } from "reactstrap";
import classnames from "classnames";

// components
import AppSimpleBar from "./AppSimpleBar";

// hooks (já sem Redux)
import { useProfile, useContactsHook as useContacts } from "../hooks";

// utils
import { DivideByKeyResultTypes, divideByKey } from "../utils";
import EmptyStateContacts from "./EmptyStateResult";
import { IMessagesTypes } from "../types/message";
import { ContactTypes } from "../types/contacts";

/* --------------------- Forward Message --------------------- */
interface ForwardMessageProps {
  forwardData: null | IMessagesTypes | undefined;
  chatUserDetails: any;
}

const ForwardMessage = ({ forwardData, chatUserDetails }: ForwardMessageProps) => {
  const { userProfile } = useProfile();

  const replyUserName = chatUserDetails?.firstName
    ? `${chatUserDetails.firstName} ${chatUserDetails.lastName}`
    : "-";

  const isReplyFromMe =
    forwardData && forwardData.meta.sender + "" === userProfile?.id + "";

  return (
    <div className="replymessage-block mb-2">
      <h5 className="conversation-name">
        {isReplyFromMe ? "You" : replyUserName}
      </h5>

      {forwardData?.text && <p className="mb-0">{forwardData.text}</p>}

      {(forwardData?.image || forwardData?.attachments) && (
        <p className="mb-0">
          {forwardData.attachments && !forwardData.image &&
            `${forwardData.attachments.length} Files`}

          {forwardData.image && !forwardData.attachments &&
            `${forwardData.image.length} Images`}

          {forwardData.image && forwardData.attachments &&
            `${forwardData.attachments.length} Files & ${forwardData.image.length} Images`}
        </p>
      )}
    </div>
  );
};

/* --------------------- Contact Item --------------------- */
interface ContactItemProps {
  contact: ContactTypes;
  selected: boolean;
  onSelectContact: (id: string | number, selected: boolean) => void;
  onSend: (contact: Array<number | string>) => void;
}

const ContactItem = ({
  contact,
  selected,
  onSelectContact,
  onSend,
}: ContactItemProps) => {
  const fullName = `${contact.firstName} ${contact.lastName}`;

  const onClick = () => {
    onSelectContact(contact.id, !selected);
    if (!selected) onSend([contact.id]);
  };

  return (
    <li>
      <div className="d-flex align-items-center">
        <div className="flex-grow-1">
          <h5 className="font-size-14 m-0">{fullName}</h5>
        </div>
        <div className="flex-shrink-0">
          {!selected ? (
            <Button size="sm" color="primary" onClick={onClick}>Send</Button>
          ) : (
            <Button size="sm" color="light" onClick={onClick}>Undo</Button>
          )}
        </div>
      </div>
    </li>
  );
};

/* --------------------- Group by Letter --------------------- */
interface CharacterItemProps {
  letterContacts: DivideByKeyResultTypes<any>;
  index: number;
  totalContacts: number;
  selectedContacts: Array<number | string>;
  onSelectContact: (id: string | number, selected: boolean) => void;
  onSend: (contact: Array<number | string>) => void;
}

const CharacterItem = ({
  letterContacts,
  index,
  totalContacts,
  selectedContacts,
  onSelectContact,
  onSend,
}: CharacterItemProps) => {
  return (
    <div className={classnames({ "mt-3": index !== 0 })}>
      <div className="contact-list-title">{letterContacts.letter}</div>

      <ul
        className={classnames("list-unstyled", "contact-list", {
          "mb-0": index + 1 === totalContacts,
        })}
      >
        {(letterContacts.data || []).map((contact, key) => {
          const selected = selectedContacts.includes(contact.id);
          return (
            <ContactItem
              key={key}
              contact={contact}
              selected={selected}
              onSelectContact={onSelectContact}
              onSend={onSend}
            />
          );
        })}
      </ul>
    </div>
  );
};

/* --------------------- Forward Modal --------------------- */
interface ForwardModalProps {
  isOpen: boolean;
  onClose: () => void;
  forwardData: null | IMessagesTypes | undefined;
  chatUserDetails: any;
  onForward: (data: any) => void;
}

const ForwardModal = ({
  isOpen,
  onClose,
  forwardData,
  chatUserDetails,
  onForward,
}: ForwardModalProps) => {

  // contatos vindos do hook (SEM REDUX)
  const { categorizedContacts, totalContacts } = useContacts();


  const [contacts, setContacts] = useState<typeof categorizedContacts>(categorizedContacts);

  useEffect(() => {
    setContacts(categorizedContacts);
  }, [categorizedContacts]);

  /* SEARCH */
  const [search, setSearch] = useState("");

  const onChangeSearch = (value: string) => {
    setSearch(value);

    if (!value.trim()) {
      setContacts(categorizedContacts);
      return;
    }

    const filtered = categorizedContacts.filter(c =>
      c.letter.toLowerCase().includes(value.toLowerCase())
    );

    //setContacts(divideByKey("firstName", filtered));
    setContacts(filtered);
  };

  const totalC = contacts.length;

  /* SELECT */
  const [selectedContacts, setSelectedContacts] = useState<Array<string | number>>([]);

  const onSelectContact = (id: string | number, selected: boolean) => {
    setSelectedContacts(prev =>
      selected ? [...prev, id] : prev.filter(x => x + "" !== id + "")
    );
  };

  /* MESSAGE */
  const [message, setMessage] = useState<string | null | undefined>("");

  /* SEND ALL */
  const onShareAll = () => {
    const allIds = categorizedContacts.map(c => c.letter);
    setSelectedContacts(allIds);

    onForward({
      contacts: allIds,
      message,
    });
  };

  /* SEND SINGLE */
  const onSend = (contactsIds: Array<number | string>) => {
    onForward({
      contacts: contactsIds,
      message,
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered className="forwardModal">
      <ModalHeader
        className="modal-title-custom text-white font-size-16"
        toggle={onClose}
      >
        Share this Message
      </ModalHeader>

      <ModalBody className="p-4">
        <ForwardMessage
          forwardData={forwardData}
          chatUserDetails={chatUserDetails}
        />

        <textarea
          className="form-control"
          placeholder="Type your message..."
          rows={2}
          value={message || ""}
          onChange={(e) => setMessage(e.target.value)}
        />

        <hr className="my-4" />

        <div className="input-group mb-3">
          <input
            type="text"
            className="form-control bg-light border-0 pe-0"
            placeholder="Search here.."
            value={search}
            onChange={(e) => onChangeSearch(e.target.value)}
          />
          <button className="btn btn-light" type="button">
            <i className="bx bx-search align-middle"></i>
          </button>
        </div>

        {totalC === 0 ? (
          <EmptyStateContacts searchedText={search} />
        ) : (
          <>
            <div className="d-flex align-items-center px-1">
              <h4 className="mb-0 font-size-11 text-muted text-uppercase flex-grow-1">
                Contacts
              </h4>

              <Button size="sm" color="primary" onClick={onShareAll}>
                Share All
              </Button>
            </div>

            <AppSimpleBar style={{ maxHeight: "150px" }} className="mx-n4 px-1">
              {contacts.map((letterGroup, idx) => (
                // <CharacterItem
                //   key={idx}
                //   letterContacts={letterGroup.name}
                //   index={idx}
                //   totalContacts={totalContacts}
                //   selectedContacts={selectedContacts}
                //   onSelectContact={onSelectContact}
                //   onSend={onSend}
                // />
                <h1>contato: {letterGroup.letter}</h1>
              ))}
            </AppSimpleBar>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ForwardModal;
